import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./Help.css";

const PADDING = 6;
const CARD_WIDTH = 340;
const GAP = 12;

const findTarget = (step) => document.querySelector(`[data-tour="${step.target}"]`);

const measure = (element) => {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: Math.min(rect.height, window.innerHeight * 0.6) + PADDING * 2,
  };
};

const cardPosition = (box) => {
  const width = Math.min(CARD_WIDTH, window.innerWidth - 32);
  const left = Math.max(16, Math.min(box.left, window.innerWidth - width - 16));
  const below = box.top + box.height + GAP;
  if (below + 220 < window.innerHeight) return { top: below, left, width };
  const above = box.top - GAP;
  if (above > 220) return { bottom: window.innerHeight - above, left, width };
  return { top: Math.max(16, window.innerHeight - 260), left, width };
};

export default function Tour({ steps, onClose }) {
  const available = steps.filter((step) => findTarget(step));
  const [index, setIndex] = useState(0);
  const [box, setBox] = useState(null);
  const cardRef = useRef(null);
  const step = available[index];

  const update = useCallback(() => {
    const element = step && findTarget(step);
    if (element) setBox(measure(element));
  }, [step]);

  useLayoutEffect(() => {
    const element = step && findTarget(step);
    if (!element) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
    update();
    const timer = setTimeout(update, reduce ? 0 : 400);
    return () => clearTimeout(timer);
  }, [step, update]);

  useEffect(() => {
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [update]);

  useEffect(() => {
    cardRef.current?.focus();
  }, [index]);

  const last = index === available.length - 1;
  const next = () => (last ? onClose() : setIndex(index + 1));
  const back = () => setIndex(Math.max(0, index - 1));

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (available.length === 0) onClose();
  }, [available.length, onClose]);

  if (!step || !box) return null;

  return createPortal(
    <div className="tour" role="presentation">
      <div className="tour__backdrop" onClick={onClose} />
      <div className="tour__spotlight" style={box} />
      <div
        ref={cardRef}
        className="tour__card"
        style={cardPosition(box)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-title"
        aria-describedby="tour-text"
        tabIndex={-1}
      >
        <div className="tour__step">Step {index + 1} of {available.length}</div>
        <h6 id="tour-title" className="tour__title">{step.title}</h6>
        <p id="tour-text" className="tour__text">{step.text}</p>
        <div className="d-flex align-items-center gap-2">
          {last ? (
            <span className="me-auto" />
          ) : (
            <button type="button" className="btn btn-sm btn-link px-0 me-auto" onClick={onClose}>
              Skip tour
            </button>
          )}
          {index > 0 && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={back}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-sm btn-primary" onClick={next}>
            {last ? "Done" : "Next"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
